// components/MultipleServiceSelector.tsx
// ─── RESPONSIVE PATCH ───────────────────────────────────────────────────────
// Key fixes vs original:
//  1. Service list cards: min-w-0 on flex children, truncate on title
//  2. Feature input row: flex-wrap on small screens
//  3. Photo grid: single column on xs (<480px)
//  4. Selector header: wraps properly at all breakpoints
//  5. Continue button: full-width on xs
//  6. Add-New form: textarea + select fill container properly
//  7. AI enhancement dialog: stacked comparison grid on mobile
// ────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Search,
    CheckCircle2,
    ArrowLeft,
    Layers,
    Briefcase,
    Upload,
    X,
    Loader2,
    ImageIcon,
    CheckCircle,
    AlertCircle,
    Phone,
    Mail,
    Globe,
    MapPin,
    Plus,
    Building2,
    Pencil,
    Sparkles,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_PHOTOS = 1;
const MAX_FEATURES = 10;

interface Template {
    id: number;
    name: string;
    component: React.ComponentType<any>;
    photoCount: number;
    rating: number;
    tag: string;
    uses: number;
    category: string;
}

interface FeatureItem {
    id?: number;
    feature_name: string;
    name?: string;
}

interface PhotoItem {
    id: string;
    url: string;
    file?: File;
    index: number;
    isExisting?: boolean;
}

interface Service {
    id: string;
    title: string;
    service_type: string;
    image?: string;
    photos?: string[];
    description?: string;
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
    subject?: string;
    features?: FeatureItem[];
    [key: string]: any;
}

interface NewServiceData {
    title: string;
    service_type: string;
    description: string;
    phone: string;
    email: string;
    website: string;
    address: string;
    photos: PhotoItem[];
    features: FeatureItem[];
}

interface MultipleServiceSelectorProps {
    templates?: Template[];
    savedServices?: Service[];
    loadingServices?: boolean;
    onBack: () => void;
    onContinue: (
        selectedTemplates: Template[],
        selectedServices: Service[],
        newServicesData: NewServiceData[]
    ) => void;
    getFullUrl?: (url: string | null | undefined) => string;
    onServiceAdd?: (newService: Service) => void;
    onServiceDelete?: (serviceId: string) => void;
    onRefreshServices?: () => void;
}

const MultipleServiceSelector: React.FC<MultipleServiceSelectorProps> = ({
    templates = [],
    savedServices = [],
    loadingServices = false,
    onBack,
    onContinue,
    getFullUrl: getFullUrlProp,
    onServiceAdd,
    onServiceDelete,
    onRefreshServices,
}) => {
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    const getFullUrl = useCallback(
        (url: string | null | undefined): string => {
            if (getFullUrlProp) return getFullUrlProp(url);
            if (!url) return "";
            if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
            return url;
        },
        [getFullUrlProp]
    );

    const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
    const [photoFilter, setPhotoFilter] = useState<"all" | number>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedServices, setSelectedServices] = useState<Service[]>([]);
    const [showAddNewForm, setShowAddNewForm] = useState(false);

    const [newServiceData, setNewServiceData] = useState<NewServiceData>({
        title: "", service_type: "", description: "",
        phone: "", email: "", website: "", address: "",
        photos: [], features: [],
    });

    const [isDragging, setIsDragging] = useState(false);
    const [locallyAddedServices, setLocallyAddedServices] = useState<Service[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    // AI Enhancement state
    const [enhanceDialogOpen, setEnhanceDialogOpen] = useState(false);
    const [enhanceDialogPhoto, setEnhanceDialogPhoto] = useState<PhotoItem | null>(null);
    const [enhancedPreviewUrl, setEnhancedPreviewUrl] = useState<string | null>(null);
    const [isEnhancingPreview, setIsEnhancingPreview] = useState(false);
    const [enhancedPhotoIds, setEnhancedPhotoIds] = useState<Set<string>>(new Set());
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
    const [originalLoaded, setOriginalLoaded] = useState(false);
    const [enhancedLoaded, setEnhancedLoaded] = useState(false);

    useEffect(() => { setOriginalLoaded(false); }, [originalImageUrl]);
    useEffect(() => { setEnhancedLoaded(false); }, [enhancedPreviewUrl]);

    const generateId = useCallback(
        () => `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        []
    );

    const safeTemplates = useMemo(() => templates || [], [templates]);

    const uniqueSavedServices = useMemo(() => {
        const seen = new Set<string>();
        return (savedServices || []).filter((s) => {
            const id = String(s?.id ?? "");
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [savedServices]);

    const allServices = useMemo(() => {
        const savedServiceIds = new Set(uniqueSavedServices.map(s => String(s.id)));
        const uniqueLocalServices = locallyAddedServices.filter(s => !savedServiceIds.has(String(s.id)));
        return [...uniqueLocalServices, ...uniqueSavedServices];
    }, [locallyAddedServices, uniqueSavedServices]);

    // ── Template handlers ────────────────────────────────────────────────────
    const toggleTemplateSelection = useCallback((template: Template) => {
        if (!template) return;
        setSelectedTemplates((prev) =>
            prev.some((t) => t.id === template.id)
                ? prev.filter((t) => t.id !== template.id)
                : [...prev, template]
        );
    }, []);

    const isTemplateSelected = useCallback(
        (id: number) => selectedTemplates.some((t) => t.id === id),
        [selectedTemplates]
    );

    // ── Service handlers ─────────────────────────────────────────────────────
    const toggleServiceSelection = useCallback((service: Service) => {
        setSelectedServices((prev) => {
            const isAlreadySelected = prev.some((s) => s.id === service.id);
            return isAlreadySelected ? prev.filter((s) => s.id !== service.id) : [...prev, service];
        });
    }, []);

    const isServiceSelected = useCallback(
        (id: string) => selectedServices.some((s) => s.id === id),
        [selectedServices]
    );

    const selectAllServices = useCallback(() => setSelectedServices(allServices), [allServices]);
    const clearAllServiceSelections = useCallback(() => setSelectedServices([]), []);

    // ── Form handlers ────────────────────────────────────────────────────────
    const handleTextChange = useCallback((field: keyof NewServiceData, value: string) => {
        setNewServiceData((prev) => ({ ...prev, [field]: value }));
    }, []);

    // ── Feature handlers ─────────────────────────────────────────────────────
    const addFeature = useCallback(() => {
        if (newServiceData.features.length >= MAX_FEATURES) {
            toast({ title: "Feature Limit Reached", description: `Maximum ${MAX_FEATURES} features allowed.`, variant: "destructive", duration: 1000 });
            return;
        }
        setNewServiceData((prev) => ({ ...prev, features: [...prev.features, { feature_name: "" }] }));
    }, [newServiceData.features.length, toast]);

    const handleFeatureChange = useCallback((index: number, value: string) => {
        setNewServiceData((prev) => {
            const updatedFeatures = [...prev.features];
            updatedFeatures[index] = { ...updatedFeatures[index], feature_name: value, name: value };
            return { ...prev, features: updatedFeatures };
        });
    }, []);

    const handleFeatureBlur = useCallback((index: number) => {
        setNewServiceData((prev) => {
            const feature = prev.features[index];
            if (!feature?.feature_name?.trim() && !feature?.id) {
                return { ...prev, features: prev.features.filter((_, i) => i !== index) };
            }
            return prev;
        });
    }, []);

    const handleFeatureDelete = useCallback((index: number) => {
        setNewServiceData((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
        toast({ title: "Feature Removed", description: "Feature has been deleted.", duration: 1000 });
    }, [toast]);

    // ── File handlers ────────────────────────────────────────────────────────
    const processFiles = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const currentPhotoCount = newServiceData.photos.length;
        const remainingSlots = MAX_PHOTOS - currentPhotoCount;

        if (remainingSlots <= 0) {
            toast({ title: "Maximum Photos Reached", description: `You can only upload up to ${MAX_PHOTOS} photos.`, variant: "destructive", duration: 1000 });
            return;
        }

        const filesToProcess = fileArray.slice(0, remainingSlots);
        const validPhotos: PhotoItem[] = [];

        filesToProcess.forEach((file) => {
            if (!/\.(jpe?g|png)$/i.test(file.name || "")) {
                toast({ title: "Not Supported", description: `${file.name}: only JPG, JPEG, PNG allowed.`, variant: "destructive", duration: 1000 });
                return;
            }
            if (!file.type.startsWith("image/")) {
                toast({ title: "Invalid File Type", description: `${file.name} is not an image.`, variant: "destructive", duration: 1000 });
                return;
            }
            if (file.size > MAX_IMAGE_SIZE) {
                toast({ title: "File Too Large", description: `${file.name} exceeds 5 MB.`, variant: "destructive", duration: 1000 });
                return;
            }
            validPhotos.push({ id: generateId(), url: URL.createObjectURL(file), file, index: currentPhotoCount + validPhotos.length, isExisting: false });
        });

        if (validPhotos.length > 0) {
            setNewServiceData((prev) => {
                const allPhotos = [...prev.photos, ...validPhotos];
                return { ...prev, photos: allPhotos.map((photo, idx) => ({ ...photo, index: idx })) };
            });
            setEnhancedPhotoIds(new Set());
            toast({ title: "Photos Added", description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""} uploaded!`, duration: 1000 });
        }
    }, [newServiceData.photos, toast, generateId]);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) processFiles(files);
        e.target.value = "";
    }, [processFiles]);

    const handleDragEnter = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) setIsDragging(false); }, []);
    const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); }, []);
    const handleDrop = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); const files = e.dataTransfer.files; if (files && files.length > 0) processFiles(files); }, [processFiles]);

    const removePhoto = useCallback((photoId: string) => {
        setNewServiceData((prev) => {
            const photoToRemove = prev.photos.find((p) => p.id === photoId);
            if (photoToRemove?.url.startsWith("blob:")) URL.revokeObjectURL(photoToRemove.url);
            const remaining = prev.photos.filter((p) => p.id !== photoId);
            return { ...prev, photos: remaining.map((photo, idx) => ({ ...photo, index: idx })) };
        });
        setEnhancedPhotoIds((prev) => { const s = new Set(prev); s.delete(photoId); return s; });
        toast({ title: "Photo Removed", duration: 1000 });
    }, [toast]);

    const setAsMainPhoto = useCallback((photoId: string) => {
        setNewServiceData((prev) => {
            const idx = prev.photos.findIndex((p) => p.id === photoId);
            if (idx <= 0) return prev;
            const newPhotos = [...prev.photos];
            const [photo] = newPhotos.splice(idx, 1);
            newPhotos.unshift(photo);
            return { ...prev, photos: newPhotos.map((p, i) => ({ ...p, index: i })) };
        });
        toast({ title: "Main Photo Updated", duration: 1000 });
    }, [toast]);

    useEffect(() => {
        return () => { newServiceData.photos.forEach((photo) => { if (photo.url?.startsWith("blob:")) URL.revokeObjectURL(photo.url); }); };
    }, []);

    // ── AI Enhancement handlers ──────────────────────────────────────────────
    const handleEnhanceImage = useCallback(async (photo: PhotoItem) => {
        if (!photo.file || enhancedPhotoIds.has(photo.id)) return;
        setOriginalImageUrl(photo.url);
        setEnhanceDialogPhoto({ ...photo });
        setEnhancedPreviewUrl(null);
        setIsEnhancingPreview(true);
        setEnhanceDialogOpen(true);
        try {
            const formData = new FormData();
            formData.append("image", photo.file);
            const response = await axiosInstance.post(endpoints.enhance_image, formData, { headers: { "Content-Type": "multipart/form-data" } });
            if (response.data?.success) {
                setEnhancedPreviewUrl(response.data.enhanced_image_data || response.data.enhanced_image);
            } else throw new Error("Enhancement failed");
        } catch (error: any) {
            toast({ title: "Enhancement Failed", description: error.response?.data?.error || "Please try again.", variant: "destructive", duration: 1000 });
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
        } finally {
            setIsEnhancingPreview(false);
        }
    }, [enhancedPhotoIds, toast]);

    const handleApplyEnhancement = useCallback(async () => {
        if (!enhanceDialogPhoto || !enhancedPreviewUrl) return;
        try {
            const isDataUrl = enhancedPreviewUrl.startsWith("data:");
            const enhancedFile = isDataUrl
                ? (() => {
                    const [header, base64] = enhancedPreviewUrl.split(",");
                    const mimeMatch = header.match(/data:(.*);base64/);
                    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
                    const binary = atob(base64);
                    const array = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
                    return new File([array], `enhanced_${Date.now()}.jpg`, { type: mime });
                })()
                : await (async () => {
                    const response = await fetch(enhancedPreviewUrl);
                    const blob = await response.blob();
                    return new File([blob], `enhanced_${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
                })();

            const newBlobUrl = URL.createObjectURL(enhancedFile);
            setNewServiceData((prev) => ({
                ...prev,
                photos: prev.photos.map((photo) => {
                    if (photo.id === enhanceDialogPhoto.id) {
                        if (photo.url.startsWith("blob:")) URL.revokeObjectURL(photo.url);
                        return { ...photo, url: newBlobUrl, file: enhancedFile };
                    }
                    return photo;
                }),
            }));
            setEnhancedPhotoIds((prev) => new Set(prev).add(enhanceDialogPhoto.id));
            toast({ title: "Image Enhanced ✨", description: "AI enhancement applied!", duration: 1000 });
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
        } catch (error) {
            toast({ title: "Error", description: "Failed to apply enhancement.", variant: "destructive" });
        }
    }, [enhanceDialogPhoto, enhancedPreviewUrl, toast]);

    const closeEnhancementDialog = useCallback(() => {
        setEnhanceDialogOpen(false);
        setEnhanceDialogPhoto(null);
        setOriginalImageUrl(null);
        setEnhancedPreviewUrl(null);
        setIsEnhancingPreview(false);
    }, []);

    // ── Validation ───────────────────────────────────────────────────────────
    const isValidWebsiteUrl = useCallback((url?: string | null): boolean => {
        const trimmed = (url || "").trim();
        if (!trimmed) return false;
        const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const pattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
        return pattern.test(trimmed) || pattern.test(normalized);
    }, []);

    const getValidationItems = useCallback(() => {
        const validFeatureCount = newServiceData.features.filter((f) => (f.feature_name || f.name || "").trim() !== "").length;
        return [
            { label: "Title", isValid: !!newServiceData.title.trim() },
            { label: "Service Type", isValid: !!newServiceData.service_type.trim() },
            { label: "Description", isValid: !!newServiceData.description.trim() },
            { label: `Images (${newServiceData.photos.length}/${MAX_PHOTOS})`, isValid: newServiceData.photos.length >= 1 },
            { label: `Features (${validFeatureCount})`, isValid: validFeatureCount >= 1 },
            { label: "Website Link", isValid: isValidWebsiteUrl(newServiceData.website) },
        ];
    }, [newServiceData, isValidWebsiteUrl]);

    const isNewServiceValid = useMemo(() => getValidationItems().every((item) => item.isValid), [getValidationItems]);

    // ── Save service ─────────────────────────────────────────────────────────
    const saveServiceToBackend = useCallback(async (serviceData: NewServiceData): Promise<Service | null> => {
        try {
            const normalizeUrl = (url?: string | null): string | null => {
                const trimmed = (url || "").trim();
                if (!trimmed) return null;
                return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
            };

            const validFeatures = serviceData.features
                .filter((f) => (f.feature_name || f.name || "").toString().trim().length > 0)
                .map((f) => ({ name: (f.feature_name || f.name || "").toString().trim() }));

            const websiteValue = (serviceData.website || "").trim();
            if (websiteValue && !isValidWebsiteUrl(websiteValue)) throw new Error("Invalid website URL");

            const normalizedWebsite = normalizeUrl(serviceData.website);
            const hasNewFiles = serviceData.photos.some((p) => p.file instanceof File);

            let createResponse;

            if (hasNewFiles) {
                const formData = new FormData();
                formData.append("title", serviceData.title.trim());
                formData.append("headline", serviceData.title.trim());
                formData.append("service_type", serviceData.service_type || "");
                if (serviceData.description?.trim()) formData.append("description", serviceData.description.trim());
                if (serviceData.phone?.trim()) formData.append("phone", serviceData.phone.trim());
                if (serviceData.email?.trim()) formData.append("email", serviceData.email.trim());
                if (normalizedWebsite) formData.append("website", normalizedWebsite);
                if (serviceData.address?.trim()) formData.append("address", serviceData.address.trim());
                formData.append("features", JSON.stringify(validFeatures));
                serviceData.photos.forEach((photo, index) => {
                    if (photo.file instanceof File) {
                        if (index === 0) formData.append("hero_image", photo.file, photo.file.name);
                        else if (index === 1) formData.append("photo_1", photo.file, photo.file.name);
                    }
                });
                createResponse = await axiosInstance.post(endpoints.service_listings, formData, { headers: { "Content-Type": undefined }, transformRequest: [(data) => data] });
            } else {
                const payload: any = {
                    title: serviceData.title.trim(),
                    headline: serviceData.title.trim(),
                    description: serviceData.description?.trim() || "",
                    service_type: serviceData.service_type || "",
                    phone: serviceData.phone?.trim() || "",
                    email: serviceData.email?.trim() || "",
                    address: serviceData.address?.trim() || "",
                    features: validFeatures,
                };
                if (normalizedWebsite) payload.website = normalizedWebsite;
                createResponse = await axiosInstance.post(endpoints.service_listings, payload, { headers: { "Content-Type": "application/json" } });
            }

            const serverData = createResponse.data;
            const serverFeatures = Array.isArray(serverData.features)
                ? serverData.features.map((f: any) => ({ id: f.id, feature_name: f.feature_name || f.name || "", name: f.name || f.feature_name || "" }))
                : [];

            return {
                id: String(serverData.id || serverData._id),
                title: serverData.title || serviceData.title,
                service_type: serverData.service_type || serviceData.service_type,
                description: serverData.description || serviceData.description,
                phone: serverData.phone || serviceData.phone,
                email: serverData.email || serviceData.email,
                website: serverData.website || serviceData.website,
                address: serverData.address || serviceData.address,
                image: getFullUrl(serverData.hero_image_url || serverData.image),
                photos: (Array.isArray(serverData.photos) ? serverData.photos : []).map((url: string) => getFullUrl(url)),
                subject: serverData.subject || "",
                features: serverFeatures,
            };
        } catch (error: any) {
            console.error("Error saving service:", error);
            return null;
        }
    }, [getFullUrl, isValidWebsiteUrl]);

    const handleAddNewServiceToList = useCallback(async () => {
        if (!isNewServiceValid) {
            toast({ title: "Missing Required Fields", description: "Please fill all required fields.", variant: "destructive", duration: 1000 });
            return;
        }
        if (newServiceData.website?.trim() && !isValidWebsiteUrl(newServiceData.website)) {
            toast({ title: "Invalid Website URL", description: "Please enter a valid website URL.", variant: "destructive", duration: 1000 });
            return;
        }

        setIsSaving(true);
        try {
            const savedService = await saveServiceToBackend(newServiceData);
            if (savedService) {
                newServiceData.photos.forEach((photo) => { if (photo.url?.startsWith("blob:")) URL.revokeObjectURL(photo.url); });
                if (onServiceAdd) onServiceAdd(savedService);
                setLocallyAddedServices((prev) => [savedService, ...prev]);
                setSelectedServices((prev) => [...prev, savedService]);
                setNewServiceData({ title: "", service_type: "", description: "", phone: "", email: "", website: "", address: "", photos: [], features: [] });
                setEnhancedPhotoIds(new Set());
                setShowAddNewForm(false);
                toast({ title: "✅ Service Added", description: `Service saved with ${savedService.features?.length || 0} features!`, duration: 1000 });
            } else {
                toast({ title: "Save Failed", description: "Could not save service. Please try again.", variant: "destructive", duration: 1000 });
            }
        } catch (error: any) {
            toast({ title: "Error", description: error.response?.data?.message || "Failed to save service.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    }, [newServiceData, isNewServiceValid, toast, saveServiceToBackend, onServiceAdd, isValidWebsiteUrl]);

    const handleFinalContinue = useCallback(() => {
        if (selectedServices.length === 0) {
            toast({ title: "No Services Selected", description: "Please select at least one service.", variant: "destructive", duration: 1000 });
            return;
        }
        try {
            sessionStorage.setItem("multiSelectedServices", JSON.stringify(selectedServices));
            sessionStorage.setItem("multiSelectedServiceTemplateIds", JSON.stringify(selectedTemplates.map(t => t.id)));
        } catch (e) { }
        onContinue(selectedTemplates, selectedServices, []);
    }, [selectedTemplates, selectedServices, toast, onContinue]);

    const handleResetForm = useCallback(() => {
        newServiceData.photos.forEach((photo) => { if (photo.url?.startsWith("blob:")) URL.revokeObjectURL(photo.url); });
        setNewServiceData({ title: "", service_type: "", description: "", phone: "", email: "", website: "", address: "", photos: [], features: [] });
        setEnhancedPhotoIds(new Set());
    }, [newServiceData.photos]);

    const serviceTypes = [
        "Real Estate Agent", "Mortgage Broker", "Home Inspector", "Contractor",
        "Interior Designer", "Landscaper", "Plumbing", "Electrical", "HVAC",
        "Roofing", "Painting", "Cleaning", "Other",
    ];

    // ── Enhancement Dialog ───────────────────────────────────────────────────
    const EnhancementDialog = useMemo(() => (
        <Dialog open={enhanceDialogOpen} onOpenChange={(open) => { if (!open) closeEnhancementDialog(); }}>
            <DialogContent className="w-full max-w-5xl max-h-[90dvh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 flex-shrink-0">
                            <Sparkles className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle className="text-base sm:text-xl font-semibold">AI Image Enhancement</DialogTitle>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Compare before and after</p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {isEnhancingPreview ? (
                        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                            <div className="relative">
                                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 animate-pulse" />
                                </div>
                            </div>
                            <p className="mt-4 sm:mt-6 text-base sm:text-lg font-medium">Enhancing your image...</p>
                            <p className="mt-2 text-xs sm:text-sm text-muted-foreground">This may take a few seconds</p>
                            {originalImageUrl && (
                                <div className="mt-4 sm:mt-6 p-2 bg-muted rounded-lg">
                                    <img src={originalImageUrl} alt="Processing" className="w-24 h-20 sm:w-32 sm:h-24 object-cover rounded opacity-60" />
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Comparison grid — stacks on mobile */}
                            <div className="enhancement-compare-grid grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                {/* Original */}
                                <div className="space-y-2 sm:space-y-3">
                                    <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                                        <div className="h-3 w-3 rounded-full bg-gray-400 flex-shrink-0" />
                                        Original Image
                                    </h3>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 bg-muted min-h-[180px] sm:min-h-[200px]">
                                        {originalImageUrl ? (
                                            <>
                                                <img src={originalImageUrl} alt="Original" className="w-full h-auto max-h-[350px] sm:max-h-[450px] object-contain" onLoad={() => setOriginalLoaded(true)} />
                                                <div className="absolute top-2 left-2 bg-gray-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold">Before</div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-muted-foreground">
                                                <AlertCircle className="h-12 w-12 mb-2 opacity-30" />
                                                <p className="text-xs sm:text-sm">Original not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Enhanced */}
                                <div className="space-y-2 sm:space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                                            <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                                            Enhanced Image
                                        </h3>
                                        <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                            <Sparkles className="h-3 w-3" />
                                            AI
                                        </span>
                                    </div>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-green-500 bg-muted shadow-lg shadow-green-500/20 min-h-[180px] sm:min-h-[200px]">
                                        {enhancedPreviewUrl ? (
                                            <>
                                                <img src={enhancedPreviewUrl} alt="Enhanced" className="w-full h-auto max-h-[350px] sm:max-h-[450px] object-contain" onLoad={() => setEnhancedLoaded(true)} />
                                                <div className="absolute top-2 left-2 bg-green-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                                    <Sparkles className="h-3 w-3" />
                                                    After
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-muted-foreground">
                                                <AlertCircle className="h-12 w-12 mb-2 opacity-30" />
                                                <p className="text-xs sm:text-sm">Preview not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Info box */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-5">
                                <div className="flex items-start gap-3 sm:gap-4">
                                    <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-blue-100 flex-shrink-0">
                                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">AI Enhancement Applied</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm text-blue-700">
                                            {["Improved lighting & contrast", "Enhanced color accuracy", "Reduced noise & artifacts", "Sharpened details"].map((feat) => (
                                                <div key={feat} className="flex items-center gap-2">
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                                                    <span>{feat}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                                <Button variant="outline" size="lg" onClick={closeEnhancementDialog} className="w-full sm:w-auto">Cancel</Button>
                                <Button
                                    size="lg"
                                    className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                                    onClick={handleApplyEnhancement}
                                    disabled={!enhancedPreviewUrl}
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Apply Enhancement
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    ), [enhanceDialogOpen, originalImageUrl, enhancedPreviewUrl, isEnhancingPreview, originalLoaded, enhancedLoaded, handleApplyEnhancement, closeEnhancementDialog]);

    // ── Key Features section ─────────────────────────────────────────────────
    const KeyFeaturesSection = useMemo(() => {
        const allSuggestions = ["Free Consultation", "24/7 Support", "Licensed & Insured", "Satisfaction Guaranteed", "Fast Response", "Competitive Pricing"];
        const addedFeatureNames = new Set(newServiceData.features.map((f) => (f.feature_name || f.name || "").trim().toLowerCase()));
        const availableSuggestions = allSuggestions.filter((s) => !addedFeatureNames.has(s.toLowerCase()));

        return (
            <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <Label className="flex items-center gap-2 text-sm">
                            <Sparkles className="h-4 w-4 text-primary" />
                            Key Features <span className="text-destructive">*</span>
                        </Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">Add up to {MAX_FEATURES} features</p>
                    </div>
                    <span className={`text-xs font-medium ${newServiceData.features.length >= MAX_FEATURES ? "text-orange-600" : "text-muted-foreground"}`}>
                        {newServiceData.features.filter((f) => (f.feature_name || "").trim()).length}/{MAX_FEATURES}
                    </span>
                </div>

                <div className="space-y-2">
                    {newServiceData.features.map((feature, index) => (
                        <div key={feature.id || `feature-${index}`} className="flex items-center gap-2 group">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium text-primary">{index + 1}</span>
                            </div>
                            <Input
                                type="text"
                                value={feature.feature_name || ""}
                                onChange={(e) => handleFeatureChange(index, e.target.value)}
                                onBlur={() => handleFeatureBlur(index)}
                                maxLength={30}
                                placeholder="e.g., 24/7 Support..."
                                className="flex-1 min-w-0 text-sm"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleFeatureDelete(index)}
                                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>

                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    disabled={newServiceData.features.length >= MAX_FEATURES}
                    className="w-full border-dashed text-sm"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Feature
                    {newServiceData.features.length >= MAX_FEATURES && (
                        <span className="ml-2 text-xs text-muted-foreground">(Limit reached)</span>
                    )}
                </Button>

                {availableSuggestions.length > 0 && (
                    <div className="p-3 sm:p-4 bg-muted/30 rounded-lg border border-dashed">
                        <p className="text-xs text-muted-foreground mb-2 sm:mb-3">💡 Suggested features:</p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {availableSuggestions.map((suggestion) => (
                                <Button
                                    key={suggestion}
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        if (newServiceData.features.length < MAX_FEATURES) {
                                            setNewServiceData((prev) => ({ ...prev, features: [...prev.features, { feature_name: suggestion, name: suggestion }] }));
                                        }
                                    }}
                                    className="text-xs h-7"
                                >
                                    + {suggestion}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [newServiceData.features, addFeature, handleFeatureChange, handleFeatureBlur, handleFeatureDelete]);

    // ── Image Upload section ─────────────────────────────────────────────────
    const ImageUploadSection = useMemo(() => {
        const currentPhotoCount = newServiceData.photos.length;
        const canAddMore = currentPhotoCount < MAX_PHOTOS;
        const remainingSlots = MAX_PHOTOS - currentPhotoCount;

        return (
            <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <Label className="flex items-center gap-2 text-sm">
                            <ImageIcon className="h-4 w-4 text-primary" />
                            Service Images <span className="text-destructive">*</span>
                        </Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Upload up to {MAX_PHOTOS} photo. First photo = main image.
                        </p>
                    </div>
                    <span className={`text-xs font-medium ${currentPhotoCount >= 1 ? "text-green-600" : "text-muted-foreground"}`}>
                        {currentPhotoCount}/{MAX_PHOTOS}
                    </span>
                </div>

                {canAddMore && (
                    <div
                        ref={dropZoneRef}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`relative cursor-pointer rounded-lg border border-dashed p-4 sm:p-6 transition-all duration-200 ${isDragging ? "border-primary bg-primary/5 scale-[1.02]" : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"}`}
                    >
                        <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/jpg" onChange={handleFileInputChange} className="hidden" />
                        <div className="flex flex-col items-center justify-center text-center">
                            <div className={`mb-2 sm:mb-3 rounded-full p-2.5 sm:p-3 transition-colors ${isDragging ? "bg-primary/10" : "bg-muted"}`}>
                                <Upload className={`h-5 w-5 sm:h-6 sm:w-6 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                            </div>
                            <p className="text-xs sm:text-sm font-medium">{isDragging ? "Drop photos here" : "Drag & drop photos here"}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">or <span className="font-medium text-primary">click to browse</span></p>
                            <p className="mt-1 text-xs text-muted-foreground">PNG, JPG up to 5 MB • {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining</p>
                        </div>
                    </div>
                )}

                {currentPhotoCount > 0 && (
                    <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-1.5">
                            <Label className="text-xs text-muted-foreground">Uploaded ({currentPhotoCount}/{MAX_PHOTOS})</Label>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Sparkles className="h-3 w-3 text-blue-500" />
                                Hover for Auto IQ
                            </p>
                        </div>

                        {/* Grid: 2-col on sm+, 1-col on xs */}
                        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                            {newServiceData.photos.map((photo, index) => {
                                const isEnhanced = enhancedPhotoIds.has(photo.id);
                                return (
                                    <div
                                        key={photo.id}
                                        className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${index === 0 ? "ring-2 ring-primary ring-offset-2 shadow-lg shadow-primary/20" : "ring-1 ring-border hover:ring-2 hover:ring-primary/50"} ${isEnhanced ? "ring-green-500 ring-offset-2" : ""}`}
                                    >
                                        {index === 0 && (
                                            <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground shadow-lg">
                                                <CheckCircle2 className="h-3 w-3" /> Main
                                            </div>
                                        )}
                                        {isEnhanced && (
                                            <div className="absolute left-2 bottom-2 z-20 flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-2 py-0.5 text-xs font-semibold text-white shadow-lg">
                                                <Sparkles className="h-3 w-3" /> Enhanced
                                            </div>
                                        )}
                                        <div className="absolute right-9 top-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white">
                                            {index + 1}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }}
                                            className="absolute right-2 top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow-lg ring-2 ring-white/80 hover:bg-red-600 hover:scale-110 active:scale-95 transition-all"
                                        >
                                            <X className="h-4 w-4" strokeWidth={2.5} />
                                        </button>
                                        <div className="aspect-[4/3] bg-muted">
                                            <img src={photo.url} alt={`Photo ${index + 1}`} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                                        </div>
                                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 sm:gap-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100">
                                            {photo.file && !isEnhanced && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleEnhanceImage(photo); }}
                                                    className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-lg hover:scale-105 active:scale-95 transition-all"
                                                >
                                                    <Sparkles className="h-3.5 w-3.5" /> ✨ Auto IQ
                                                </button>
                                            )}
                                            {isEnhanced && (
                                                <div className="flex items-center gap-2 rounded-lg bg-green-600/90 px-3 py-1.5 text-xs sm:text-sm font-medium text-white">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Enhanced
                                                </div>
                                            )}
                                            {index !== 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setAsMainPhoto(photo.id); }}
                                                    className="flex items-center gap-1.5 rounded-lg bg-white/20 backdrop-blur-md px-3 py-1.5 text-xs sm:text-sm font-medium text-white border border-white/30 hover:bg-white/30 hover:scale-105 active:scale-95 transition-all"
                                                >
                                                    <ImageIcon className="h-3.5 w-3.5" /> Set as Main
                                                </button>
                                            )}
                                        </div>
                                        {index === 0 && <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary to-primary/60" />}
                                        {isEnhanced && <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500" />}
                                    </div>
                                );
                            })}

                            {canAddMore && (
                                <div onClick={() => fileInputRef.current?.click()} className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50 transition-all">
                                    <Plus className="mb-1 h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground" />
                                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">Add Photo</span>
                                    <span className="text-xs text-muted-foreground mt-0.5">{remainingSlots} remaining</span>
                                </div>
                            )}
                        </div>

                        {newServiceData.photos.some((p) => p.file && !enhancedPhotoIds.has(p.id)) && (
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                {newServiceData.photos.filter((p) => p.file && !enhancedPhotoIds.has(p.id)).map((photo) => (
                                    <Button key={photo.id} type="button" variant="outline" size="sm" onClick={() => handleEnhanceImage(photo)} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 hover:from-blue-100 hover:to-indigo-100 text-xs">
                                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                                        Enhance Photo {photo.index + 1}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!canAddMore && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-green-700 flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                            Maximum {MAX_PHOTOS} photo uploaded
                        </p>
                    </div>
                )}
            </div>
        );
    }, [newServiceData.photos, isDragging, enhancedPhotoIds, handleDragEnter, handleDragOver, handleDragLeave, handleDrop, handleFileInputChange, removePhoto, setAsMainPhoto, handleEnhanceImage]);

    // ── Validation section ───────────────────────────────────────────────────
    const ValidationSection = useMemo(() => {
        const validationItems = getValidationItems();
        const completedCount = validationItems.filter((item) => item.isValid).length;
        const isComplete = completedCount === validationItems.length;
        return (
            <div className={`rounded-lg border p-3 sm:p-4 transition-colors ${isComplete ? "border-green-200 bg-green-50" : "border-border bg-muted/30"}`}>
                <div className="flex items-center justify-between mb-2 sm:mb-3 flex-wrap gap-2">
                    <h4 className="font-medium text-primary text-sm">Validation Status</h4>
                    <span className={`text-xs font-medium ${isComplete ? "text-green-600" : "text-muted-foreground"}`}>
                        {completedCount}/{validationItems.length} complete
                    </span>
                </div>
                <div className="validation-grid grid gap-1.5 sm:gap-2 text-xs sm:text-sm">
                    {validationItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-1.5 sm:gap-2">
                            {item.isValid ? <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-green-600" /> : <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-muted-foreground" />}
                            <span className={item.isValid ? "text-foreground" : "text-muted-foreground"}>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [getValidationItems]);

    // ── Add New Service Form ─────────────────────────────────────────────────
    const AddNewServiceForm = useMemo(() => (
        <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 bg-muted/30 rounded-lg border border-border">
            <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                    <h4 className="font-semibold text-foreground text-sm sm:text-base">Service Details</h4>
                </div>

                {/* Title */}
                <div className="space-y-1.5">
                    <Label htmlFor="multi-service-title" className="text-xs sm:text-sm font-medium">
                        Service Title <span className="text-red-500">*</span>
                    </Label>
                    <Input id="multi-service-title" type="text" placeholder="Enter service title" value={newServiceData.title} onChange={(e) => handleTextChange("title", e.target.value)} className="h-10 sm:h-11 bg-background text-sm w-full" />
                </div>

                {/* Service Type */}
                <div className="space-y-1.5">
                    <Label htmlFor="multi-service-type" className="text-xs sm:text-sm font-medium">
                        Service Type <span className="text-red-500">*</span>
                    </Label>
                    <select
                        id="multi-service-type"
                        value={newServiceData.service_type}
                        onChange={(e) => handleTextChange("service_type", e.target.value)}
                        className="w-full h-10 sm:h-11 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                        <option value="">Select service type</option>
                        {serviceTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                    <Label htmlFor="multi-service-description" className="text-xs sm:text-sm font-medium">
                        Description <span className="text-red-500">*</span>
                    </Label>
                    <textarea
                        id="multi-service-description"
                        placeholder="Enter service description"
                        value={newServiceData.description}
                        onChange={(e) => handleTextChange("description", e.target.value)}
                        className="w-full min-h-[90px] sm:min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                    />
                </div>

                {/* Website */}
                <div className="space-y-1.5">
                    <Label htmlFor="multi-service-website" className="text-xs sm:text-sm font-medium">
                        Website Link <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="multi-service-website" type="url" placeholder="https://www.example.com/service" value={newServiceData.website} onChange={(e) => handleTextChange("website", e.target.value)} className="h-10 sm:h-11 pl-9 bg-background text-sm w-full" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                        We'll add <span className="font-medium">https://</span> automatically if missing.
                    </p>
                </div>
            </div>

            {ImageUploadSection}
            {KeyFeaturesSection}
            {ValidationSection}

            {/* Form actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                <Button variant="outline" onClick={handleResetForm} disabled={isSaving} className="w-full sm:w-auto text-sm">Reset</Button>
                <Button variant="outline" onClick={() => { handleResetForm(); setShowAddNewForm(false); }} disabled={isSaving} className="w-full sm:w-auto text-sm">Cancel</Button>
                <Button onClick={handleAddNewServiceToList} disabled={!isNewServiceValid || isSaving} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 text-sm">
                    {isSaving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>) : (<><Plus className="h-4 w-4 mr-2" />Add Service</>)}
                </Button>
            </div>
        </div>
    ), [newServiceData, isNewServiceValid, isSaving, handleTextChange, handleResetForm, handleAddNewServiceToList, ImageUploadSection, KeyFeaturesSection, ValidationSection, serviceTypes]);

    // ── Services list ────────────────────────────────────────────────────────
    const ServicesListWithAddNew = useMemo(() => (
        <div className="space-y-3 sm:space-y-4">
            {/* Header row */}
            <div className="selector-header-row flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 flex-wrap">
                <Label className="text-sm sm:text-base font-medium flex items-center gap-2">
                    <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    Select Services
                </Label>
                <div className="action-chips flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <Badge variant="secondary" className="h-7 sm:h-8 px-2.5 sm:px-3 flex items-center text-xs">
                        {selectedServices.length} selected
                    </Badge>
                    {allServices.length > 0 && (
                        <>
                            <Button size="sm" variant="outline" className="h-7 sm:h-8 text-xs px-2.5" onClick={selectAllServices} disabled={allServices.length === 0}>Select All</Button>
                            <Button size="sm" variant="outline" className="h-7 sm:h-8 text-xs px-2.5" onClick={clearAllServiceSelections} disabled={selectedServices.length === 0}>Clear</Button>
                        </>
                    )}
                    {!showAddNewForm && (
                        <Button variant="outline" size="sm" onClick={() => setShowAddNewForm(true)} className="gap-1.5 h-7 sm:h-8 text-xs">
                            <Plus className="h-3.5 w-3.5" /> Add New
                        </Button>
                    )}
                </div>
            </div>

            {showAddNewForm && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                    {AddNewServiceForm}
                </div>
            )}

            {loadingServices ? (
                <div className="flex items-center justify-center py-10 sm:py-12">
                    <Loader2 className="h-7 w-7 sm:h-8 sm:w-8 animate-spin text-primary mr-3" />
                    <p className="text-sm text-muted-foreground">Loading services...</p>
                </div>
            ) : allServices.length === 0 && !showAddNewForm ? (
                <div className="text-center py-10 sm:py-12 bg-muted/20 rounded-lg border-2 border-dashed border-muted">
                    <Briefcase className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                    <p className="text-sm text-muted-foreground mb-3 sm:mb-4">No services found.</p>
                    <Button onClick={() => setShowAddNewForm(true)} className="gap-2 text-sm">
                        <Plus className="h-4 w-4" /> Add Your First Service
                    </Button>
                </div>
            ) : (
               <div className="property-scroll-list space-y-2 sm:space-y-3 pr-1 overflow-y-auto max-h-[420px] sm:max-h-[500px]">
                    {allServices.map((service) => {
                        if (!service) return null;
                        const isSelected = isServiceSelected(service.id);
                        const mainImage = (service.hero_image_url || service.image)
                            ? getFullUrl(service.hero_image_url || service.image)
                            : null;
                        const isNewlyAdded = locallyAddedServices.some(s => s.id === service.id);

                        return (
                            <div
                                key={service.id}
                                onClick={() => toggleServiceSelection(service)}
                                className={`group relative border rounded-lg p-3 sm:p-4 bg-background transition-all duration-200 cursor-pointer min-w-0 overflow-hidden max-w-full w-full ${isSelected ? "border-primary ring-2 ring-primary/30 shadow-md" : "border-border hover:shadow-lg hover:border-primary/30"}`}
                            >
                                {/* Checkbox */}
                                <div className="absolute top-3 left-3 z-10">
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => toggleServiceSelection(service)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 sm:h-5 sm:w-5 bg-white"
                                    />
                                </div>

                                {isSelected && (
                                    <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10">
                                        <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                                    </div>
                                )}

                                {isNewlyAdded && (
                                    <div className="absolute top-2.5 right-9 sm:top-3 sm:right-12 z-10">
                                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs px-1.5 py-0.5">New</Badge>
                                    </div>
                                )}

                                {/* Card content — flex-col on xs, flex-row on sm */}
                                <div className="flex flex-row items-start gap-2 sm:gap-3 pl-7 sm:pl-8 pr-6 sm:pr-8 min-w-0 overflow-hidden w-full">
                                    {/* Thumbnail */}
                                    {mainImage ? (
                                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                                            <img
                                                key={mainImage}
                                                src={mainImage}
                                                alt={service.title || "Service"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                            <Briefcase className="h-6 w-6 sm:h-7 sm:w-7 text-muted-foreground" />
                                        </div>
                                    )}


                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3
                                            className="font-semibold text-primary text-sm sm:text-base mb-0.5 sm:mb-1 line-clamp-2 break-words"
                                            title={service.title || "Untitled Service"}
                                        >
                                            {service.title || "Untitled Service"}
                                        </h3>
                                        {service.service_type && (
                                            <Badge variant="secondary" className="text-xs mb-1.5 whitespace-normal">
                                                {service.service_type}
                                            </Badge>
                                        )}
                                        {service.address && (
                                            <p className="text-xs text-muted-foreground mb-1 flex items-start gap-1 min-w-0">
                                                <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                                <span className="line-clamp-2 break-words flex-1 min-w-0" title={service.address}>
                                                    {service.address}
                                                </span>
                                            </p>
                                        )}
                                        {service.features && service.features.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                {service.features.slice(0, 3).map((f: any, idx: number) => {
                                                    const label = (f.name || f.feature_name || "").toString().trim();
                                                    if (!label) return null;
                                                    return (
                                                        <Badge
                                                            key={f.id || idx}
                                                            variant="outline"
                                                            className="text-xs px-2 py-0.5 whitespace-normal h-auto leading-snug"
                                                        >
                                                            {label}
                                                        </Badge>
                                                    );
                                                })}
                                                {service.features.length > 3 && (
                                                    <Badge variant="outline" className="text-xs px-2 py-0.5 flex-shrink-0">
                                                        +{service.features.length - 3} more
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                                            {service.phone && (
                                                <p className="text-xs text-muted-foreground flex items-center gap-1 min-w-0 overflow-hidden">
                                                    <Phone className="h-3 w-3 flex-shrink-0" />
                                                    <span className="truncate flex-1 min-w-0" title={service.phone}>
                                                        {service.phone}
                                                    </span>
                                                </p>
                                            )}
                                            {service.website && (
                                                <span className="flex items-center gap-1 text-blue-600 text-xs">
                                                    <Globe className="h-3 w-3 flex-shrink-0" /> Link
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    ), [allServices, loadingServices, selectedServices, showAddNewForm, locallyAddedServices, AddNewServiceForm, getFullUrl, toggleServiceSelection, isServiceSelected, selectAllServices, clearAllServiceSelections]);

    // ── Main render ──────────────────────────────────────────────────────────
    return (
        <div className="mx-auto w-full max-w-6xl px-4">
            {/* <h2 className="page-title text-primary mb-6 sm:mb-8 text-center text-xl sm:text-2xl font-bold">
                Select Multiple Templates &amp; Services
            </h2> */}

            <Card className="w-full">
                <CardContent className="space-y-4 p-4 pt-6 sm:space-y-6">
                    {ServicesListWithAddNew}
                </CardContent>
            </Card>

            {/* Footer nav */}
            <div className="flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-3 mt-4 sm:mt-6">
                <Button variant="outline" onClick={onBack} className="w-full sm:w-auto text-sm">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                    disabled={selectedServices.length === 0}
                    onClick={handleFinalContinue}
                    className={`w-full sm:w-auto text-sm text-white ${selectedServices.length > 0 ? "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700" : "bg-blue-200 cursor-not-allowed"}`}
                >
                    Continue with {selectedServices.length} Service{selectedServices.length !== 1 ? 's' : ''}
                </Button>
            </div>

            {EnhancementDialog}
        </div>
    );
};

export default MultipleServiceSelector;